import { useState, FC } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/css";
import { Plus, ChevronRight } from "lucide-react";
import swipeContent from "./articles.json";
import { Autoplay } from "swiper/modules";
import { Link, NavLink } from "react-router-dom";
import Header from "@/components/landing/layout/Header";
import Footer from "@/components/landing/layout/Footer";
import GettingStarted from "@/components/landing/common/GettingStarted";

// Type for article items (based on your JSON structure)
interface ArticleItem {
  id: any;
  image: string;
  topic: string;
  heading: string;
}

const Articles: FC = () => {
  const categories: string[] = [
    "RETIRES & EXCHANGES",
    "SALES",
    "UPDATES",
    "SHIPPING PROTECTION",
    "SHIPPING AND FULLFULLMENT",
    "CUSTOMER LOYALTY",
    "PRODUCT PROTECTION",
    "RETAIL GROWTH",
    "CUSTOMER EXPERIENCES",
  ];

  const [showCategories, setShowCategories] = useState<boolean>(false);

  return (
    <>
      <Header />
      <div className="tw-bg-black tw-object-center tw-pt-10 tw-text-white">
        <header className="tw-py-20 tw-mx-auto tw-max-w-4xl lg:tw-mx-45">
          <h3 className="tw-font-extralight tw-mb-1 tw-text-xl">ARTICLES</h3>
          <h1 className="tw-text-4xl md:tw-text-5xl tw-font-bold tw-tracking-wide">
            Discover our latest articles,<br /> feature releases, and more!
          </h1>
        </header>

        {/* Swiper */}
        <div className="tw-pl-5" data-aos="fade-in">
          <Swiper
            spaceBetween={50}
            slidesPerView={1.2}
            loop={true}
            autoplay={{
              delay: 3000,
              disableOnInteraction: false,
            }}
            modules={[Autoplay]}
            className="tw-h-[75vh]"
          >
            {(swipeContent as ArticleItem[]).map((item, key) => (
              <SwiperSlide
                style={{ backgroundImage: `url(${item.image})` }}
                className="tw-rounded-lg tw-bg-cover tw-bg-center tw-relative tw-text-white tw-h-[75vh]"
                key={key}
              >
                <div
                  data-aos="fade-up"
                  className="md:tw-w-1/3 tw-absolute lg:tw-static tw-bottom-0 tw-m-3 lg:tw-m-8 tw-p-5 md:tw-p-8 tw-rounded-xl tw-bg-[#0C0C0C]"
                >
                  <span className="tw-inline-block tw-text-xs tw-p-2 tw-rounded-sm tw-mb-4 tw-bg-[#003D4E]">
                    {item.topic}
                  </span>
                  <h1 className="md:tw-text-3xl tw-text-xl tw-tracking-wide tw-font-medium tw-mb-6">
                    {item.heading}
                  </h1>
                  <Link to={item.id} className="tw-bg-[#00C5FB] tw-text-[#0C0C0C] tw-rounded-3xl tw-px-6 tw-py-3">
                    Learn More
                  </Link>
                </div>
              </SwiperSlide>
            ))}
          </Swiper>
        </div>

        {/* Categories */}
        <div className="tw-bg-[#0C0C0C] lg:tw-px-30 tw-px-16 tw-py-10 lg:tw-py-15 tw-my-10" data-aos="fade-in">
          <div className="lg:tw-max-w-[70vw] tw-mx-auto">

            <div
              onClick={() => setShowCategories(!showCategories)}
              className="tw-flex tw-justify-between tw-items-center  tw-text-2xl tw-tracking-wide tw-font-semibold tw-w-full tw-cursor-pointer"
            >
              <h1>EXPLORE CATEGORIES</h1>
              <span className={`tw-transition-all ${showCategories ? "tw-rotate-45" : ""}`}>
                <Plus />
              </span>
            </div>

            <div
              className={`  tw-transition-all ${showCategories
                ? "tw-max-h-[500px]  tw-mt-5"
                : "tw-max-h-0 tw-overflow-hidden"
                }`}
            >
              <span className="tw-text-md">POPULAR CATEGORIES</span>
              <div className="tw-mt-10 tw-text-sm tw-flex tw-gap-5 tw-flex-wrap">
                {categories.map((item, key) => (
                  <span
                    key={key}
                    className="tw-rounded-md tw-bg-[#151516CC] tw-py-2 tw-px-6"
                  >
                    {item}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Grid Section */}
        <div className="tw-grid tw-grod-cols-1 lg:tw-grid-cols-2 tw-w-[70vw] tw-mx-auto tw-mt-5 tw-gap-x-10 tw-gap-y-5">
          {(swipeContent as ArticleItem[]).map((items, key) => (
            <NavLink
              to={`/articles/${items.id}`}
              key={key}
              className="tw-flex tw-flex-col tw-cursor-pointer hover:tw-shadow-[#6ddffe] tw-h-130 hover:tw-shadow-[0_0_20px_#6ddffe] tw-duration-300 tw-transition-all hover:tw-ring-1 hover:tw-scale-105 tw-bg-[#0C0C0C] tw-w-full tw-rounded-lg tw-overflow-hidden"
            >
              <img
                src={items.image}
                alt="article"
                className="tw-w-full tw-h-60   tw-object-cover"
              />
              <div className="md:tw-w-[80%] tw-px-6 tw-py-5 tw-flex tw-flex-col tw-flex-1">
                <h1 className="lg:tw-text-3xl tw-text-xl tw-pb-8 tw-font-semibold">
                  {items.heading}
                </h1>
                <NavLink
                  to={`/articles/${items.id}`}
                  className="tw-mt-auto tw-flex tw-items-center tw-self-start tw-font-light"
                >
                  Learn More <ChevronRight />
                </NavLink>
              </div>
            </NavLink>
          ))}
        </div>
        <GettingStarted />
      </div>
      <Footer />
    </>
  );
};

export default Articles;
